import { useResolvedResource } from '@Pages/pageRuntime/context/SdkResourceResolverContext';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import { assertResourceMethodAvailable } from '@Pages/pageRuntime/sdk/endpointGating';
import type {
  ApiError,
  ResourceRecord,
  SdkRecordHandle,
  SdkReferenceRecord,
  SdkReferenceRelationPath,
  SdkResourceQueryReference,
  SdkUseRecordOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { usePollCountdown, usePollRefetchInterval } from './pollInterval';

/**
 * Reactive wrapper around `resource.urls.show(recordId)` for fetching a single
 * record. The page counterpart to `@Core/api/RecordApi.useRecord`, accepting
 * a `SdkResource` instead of a raw `resourceId` string.
 *
 * Exposed via `Sdk.hooks.useRecord(resource, recordId)`.
 *
 * A nullish `resource`, or an id / slug the viewer cannot access, keeps the hook idle
 * instead of throwing.
 */
export function useRecord<
  TReference extends SdkResourceQueryReference<'show'>,
  const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
>(
  resource: TReference,
  recordId: string | null | undefined,
  options?: SdkUseRecordOptions<TRelations, SdkReferenceRecord<TReference, TRelations>>
): SdkRecordHandle<SdkReferenceRecord<TReference, TRelations>>;
export function useRecord(
  reference: SdkResourceQueryReference<'show'>,
  recordId: string | null | undefined,
  options?: SdkUseRecordOptions
): SdkRecordHandle {
  const resource = useResolvedResource(reference);
  const queryClient = useQueryClient();
  const poll = usePollRefetchInterval<ResourceRecord, ResourceRecord | undefined>(
    options?.pollInterval,
    (data) => data
  );

  const relations = options?.relations ?? [];
  const callerEnabled = options?.enabled ?? true;
  const scope = options?.scope;

  const resourceId = resource?.id;
  const resourceName = resource?.name ?? '';
  const disabledEndpoints = resource?.disabled_data_api_endpoints ?? [];
  const showUrl =
    resource && 'urls' in resource && recordId ? resource.urls.show(recordId) : undefined;
  const queryKey = [
    'record',
    resourceId,
    recordId,
    relations,
    scope,
    showUrl,
    resourceName,
    disabledEndpoints
  ];

  const enabled = callerEnabled && showUrl !== undefined;

  const query = useQuery<ResourceRecord, ApiError>({
    enabled,
    queryKey,
    refetchInterval: poll.refetchInterval,
    queryFn: () => {
      assertResourceMethodAvailable(
        { name: resourceName, disabled_data_api_endpoints: disabledEndpoints },
        'get'
      );

      const params: Record<string, unknown> = {};
      if (relations.length > 0) {
        params.relations = relations;
      }
      if (scope) {
        params.$scope = scope;
      }
      return axios
        .get(showUrl as string, {
          params: Object.keys(params).length > 0 ? params : undefined
        })
        .then((response) => response.data.data)
        .catch((error) => {
          throw toApiError(error);
        });
    }
  });

  const refreshIn = usePollCountdown(poll, query, enabled);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refreshIn,
    invalidate: () => queryClient.invalidateQueries({ queryKey })
  };
}
